import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  AiRoleRoutingConfiguration,
  AiRoutingRole,
  CheckModelCapabilitiesRequest,
  ProviderModelCapabilityResult,
  UpdateAiRoleRoutingRequest,
} from '@claude-code-api/shared';
import { Bot, Code2, Eye, Pencil, RefreshCw, Save } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';

interface ApiProviderRow {
  id: string;
  name: string;
  modelName: string | null;
  isEnabled: boolean;
  isActive: boolean;
}

interface RoleRoutingRowProps {
  configuration: AiRoleRoutingConfiguration;
  providers: ApiProviderRow[];
  checking: boolean;
  capabilityResults: ProviderModelCapabilityResult[];
  onCheckCapabilities: () => void;
  onEdit: () => void;
}

interface EditRoutingDialogProps {
  configuration: AiRoleRoutingConfiguration | null;
  providers: ApiProviderRow[];
  saving: boolean;
  onChange: (configuration: AiRoleRoutingConfiguration) => void;
  onClose: () => void;
  onSave: () => void;
}

function getProviderModels(provider: ApiProviderRow): string[] {
  return String(provider.modelName || '')
    .split(',')
    .map((modelName) => modelName.trim())
    .filter(Boolean);
}

function getMatchingProviders({
  configuration,
  providers,
}: {
  configuration: AiRoleRoutingConfiguration;
  providers: ApiProviderRow[];
}): ApiProviderRow[] {
  return providers.filter(
    (provider) =>
      provider.isEnabled &&
      getProviderModels(provider).includes(configuration.targetModel),
  );
}

function getAggregateCapabilityStatus(
  results: ProviderModelCapabilityResult[],
): ProviderModelCapabilityResult['status'] | null {
  if (results.length === 0) return null;
  if (results.some((result) => result.status === 'unavailable')) {
    return 'unavailable';
  }
  if (results.some((result) => result.status === 'vision_unverified')) {
    return 'vision_unverified';
  }
  if (results.every((result) => result.status === 'healthy')) return 'healthy';
  return 'text_only';
}

function CapabilityBadge({
  results,
}: {
  results: ProviderModelCapabilityResult[];
}) {
  const { t } = useTranslation('adminModelMappings');
  const status = getAggregateCapabilityStatus(results);
  if (!status) {
    return (
      <Badge variant="outline" className="font-normal text-muted-foreground">
        {t('capabilities.status.not_checked')}
      </Badge>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Badge
        variant={
          status === 'healthy'
            ? 'success'
            : status === 'text_only'
              ? 'warning'
              : status === 'unavailable'
                ? 'destructive'
                : 'outline'
        }
      >
        {t(`capabilities.status.${status}`)}
      </Badge>
      {status === 'healthy' ? (
        <Badge variant="outline">
          <Eye className="mr-1 h-3 w-3" />
          {t('capabilities.vision')}
        </Badge>
      ) : null}
    </div>
  );
}

function RoleRoutingRow({
  configuration,
  providers,
  checking,
  capabilityResults,
  onCheckCapabilities,
  onEdit,
}: RoleRoutingRowProps) {
  const { t } = useTranslation(['adminModelMappings', 'common']);
  const isPm = configuration.role === 'pm';
  const matchingProviders = getMatchingProviders({ configuration, providers });
  const fixedProvider = matchingProviders.find(
    (provider) => provider.id === configuration.fixedProviderId,
  );
  const visibleCapabilityResults = capabilityResults.filter(
    (result) => result.modelName === configuration.targetModel,
  );

  return (
    <li className="grid gap-4 px-4 py-4 sm:px-5 lg:grid-cols-[minmax(220px,1.35fr)_minmax(170px,1fr)_minmax(170px,1fr)_minmax(140px,.8fr)_auto] lg:items-center">
      <div className="flex min-w-0 items-center gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border bg-muted/20 text-primary">
          {isPm ? <Bot /> : <Code2 />}
        </div>
        <div className="min-w-0">
          <h2 className="text-sm font-semibold">
            {t(`adminModelMappings:roles.${configuration.role}.title`)}
          </h2>
          <p className="truncate text-xs text-muted-foreground">
            {t(`adminModelMappings:roles.${configuration.role}.description`)}
          </p>
        </div>
      </div>

      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">
          {t('adminModelMappings:summary.model')}
        </p>
        <p className="truncate text-sm font-medium">
          {configuration.targetModel}
        </p>
      </div>

      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">
          {t('adminModelMappings:summary.routing')}
        </p>
        <p className="truncate text-sm font-medium">
          {configuration.mode === 'balanced'
            ? t('adminModelMappings:routing.autoBalance')
            : fixedProvider?.name ||
              t('adminModelMappings:form.selectProvider')}
        </p>
      </div>

      <div>
        <p className="text-xs text-muted-foreground">
          {t('adminModelMappings:summary.pricing')}
        </p>
        <p className="text-sm font-medium tabular-nums">
          ${configuration.inputPrice} / ${configuration.outputPrice}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2 lg:justify-end">
        <CapabilityBadge results={visibleCapabilityResults} />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onCheckCapabilities}
          disabled={matchingProviders.length === 0 || checking}
        >
          <RefreshCw className={checking ? 'animate-spin' : ''} />
          {checking
            ? t('adminModelMappings:capabilities.checking')
            : t('adminModelMappings:capabilities.checkShort')}
        </Button>
        <Button type="button" size="sm" onClick={onEdit}>
          <Pencil />
          {t('adminModelMappings:actions.edit')}
        </Button>
      </div>
    </li>
  );
}

function EditRoutingDialog({
  configuration,
  providers,
  saving,
  onChange,
  onClose,
  onSave,
}: EditRoutingDialogProps) {
  const { t } = useTranslation(['adminModelMappings', 'common']);
  if (!configuration) return null;

  const enabledProviders = providers.filter((provider) => provider.isEnabled);
  const targetModels = Array.from(
    new Set([
      ...enabledProviders.flatMap(getProviderModels),
      ...(configuration.targetModel ? [configuration.targetModel] : []),
    ]),
  ).sort();
  const matchingProviders = getMatchingProviders({ configuration, providers });
  const selectedTargetIsAvailable = matchingProviders.length > 0;
  const canSave =
    configuration.targetModel.length > 0 &&
    selectedTargetIsAvailable &&
    (configuration.mode === 'balanced' ||
      matchingProviders.some(
        (provider) => provider.id === configuration.fixedProviderId,
      ));

  const updateTargetModel = (targetModel: string) => {
    const nextMatchingProviders = enabledProviders.filter((provider) =>
      getProviderModels(provider).includes(targetModel),
    );
    const fixedProviderStillMatches = nextMatchingProviders.some(
      (provider) => provider.id === configuration.fixedProviderId,
    );
    onChange({
      ...configuration,
      targetModel,
      fixedProviderId: fixedProviderStillMatches
        ? configuration.fixedProviderId
        : nextMatchingProviders[0]?.id || null,
    });
  };

  const updateAutoBalance = (enabled: boolean) => {
    onChange({
      ...configuration,
      mode: enabled ? 'balanced' : 'fixed',
      fixedProviderId: enabled
        ? null
        : configuration.fixedProviderId || matchingProviders[0]?.id || null,
    });
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>
            {t('adminModelMappings:dialog.title', {
              role: t(`adminModelMappings:roles.${configuration.role}.title`),
            })}
          </DialogTitle>
          <DialogDescription>
            {t(`adminModelMappings:roles.${configuration.role}.description`)}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div className="flex items-center justify-between gap-4 rounded-md border bg-muted/20 p-3">
            <div>
              <Label htmlFor={`${configuration.role}-auto-balance`}>
                {t('adminModelMappings:routing.autoBalance')}
              </Label>
              <p className="text-xs text-muted-foreground">
                {t(
                  configuration.mode === 'balanced'
                    ? 'adminModelMappings:routing.balancedStatus'
                    : 'adminModelMappings:routing.fixedStatus',
                )}
              </p>
            </div>
            <Switch
              id={`${configuration.role}-auto-balance`}
              checked={configuration.mode === 'balanced'}
              onCheckedChange={updateAutoBalance}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor={`${configuration.role}-target-model`}>
              {t('adminModelMappings:form.targetModel')}
            </Label>
            <Select
              value={configuration.targetModel}
              onValueChange={updateTargetModel}
            >
              <SelectTrigger id={`${configuration.role}-target-model`}>
                <SelectValue
                  placeholder={t('adminModelMappings:form.selectTarget')}
                />
              </SelectTrigger>
              <SelectContent>
                {targetModels.map((modelName) => (
                  <SelectItem key={modelName} value={modelName}>
                    {modelName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {selectedTargetIsAvailable
                ? t('adminModelMappings:form.targetProviderCount', {
                    count: matchingProviders.length,
                  })
                : t('adminModelMappings:form.targetUnavailable')}
            </p>
          </div>

          {configuration.mode === 'fixed' ? (
            <div className="space-y-2">
              <Label htmlFor={`${configuration.role}-provider`}>
                {t('adminModelMappings:form.provider')}
              </Label>
              <Select
                value={configuration.fixedProviderId || ''}
                onValueChange={(fixedProviderId) =>
                  onChange({ ...configuration, fixedProviderId })
                }
              >
                <SelectTrigger id={`${configuration.role}-provider`}>
                  <SelectValue
                    placeholder={t('adminModelMappings:form.selectProvider')}
                  />
                </SelectTrigger>
                <SelectContent>
                  {matchingProviders.map((provider) => (
                    <SelectItem key={provider.id} value={provider.id}>
                      {provider.name}
                      {provider.isActive
                        ? ` (${t('adminModelMappings:status.defaultProvider')})`
                        : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor={`${configuration.role}-input-price`}>
                {t('adminModelMappings:form.inputPrice')}
              </Label>
              <Input
                id={`${configuration.role}-input-price`}
                type="number"
                min="0"
                step="0.01"
                value={configuration.inputPrice}
                onChange={(event) =>
                  onChange({
                    ...configuration,
                    inputPrice: Number(event.target.value),
                  })
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor={`${configuration.role}-output-price`}>
                {t('adminModelMappings:form.outputPrice')}
              </Label>
              <Input
                id={`${configuration.role}-output-price`}
                type="number"
                min="0"
                step="0.01"
                value={configuration.outputPrice}
                onChange={(event) =>
                  onChange({
                    ...configuration,
                    outputPrice: Number(event.target.value),
                  })
                }
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common:cancel')}
          </Button>
          <Button onClick={onSave} disabled={!canSave || saving}>
            <Save />
            {saving ? t('adminModelMappings:saving') : t('common:save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminModelMappingsPage() {
  const { t } = useTranslation(['adminModelMappings', 'common']);
  const [configurations, setConfigurations] = useState<
    AiRoleRoutingConfiguration[]
  >([]);
  const [providers, setProviders] = useState<ApiProviderRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingRole, setSavingRole] = useState<AiRoutingRole | null>(null);
  const [checkingRole, setCheckingRole] = useState<AiRoutingRole | null>(null);
  const [editingConfiguration, setEditingConfiguration] =
    useState<AiRoleRoutingConfiguration | null>(null);
  const [capabilityResults, setCapabilityResults] = useState<
    Partial<Record<AiRoutingRole, ProviderModelCapabilityResult[]>>
  >({});

  const orderedConfigurations = useMemo(
    () =>
      (['pm', 'coding'] as const)
        .map((role) =>
          configurations.find((configuration) => configuration.role === role),
        )
        .filter((configuration): configuration is AiRoleRoutingConfiguration =>
          Boolean(configuration),
        ),
    [configurations],
  );

  useEffect(() => {
    const loadRouting = async () => {
      try {
        const [routingResponse, providersResponse] = await Promise.all([
          fetch('/api/admin/ai-role-routing', { credentials: 'include' }),
          fetch('/api/admin/api-providers', { credentials: 'include' }),
        ]);
        if (!routingResponse.ok || !providersResponse.ok) {
          throw new Error('Routing configuration request failed');
        }
        const routingPayload = (await routingResponse.json()) as {
          data: AiRoleRoutingConfiguration[];
        };
        const providersPayload = (await providersResponse.json()) as {
          data: ApiProviderRow[];
        };
        setConfigurations(routingPayload.data);
        setProviders(providersPayload.data);
      } catch (error) {
        console.error('Failed to load AI role routing:', error);
        toast.error(t('adminModelMappings:errors.load'));
      } finally {
        setLoading(false);
      }
    };
    void loadRouting();
  }, [t]);

  const updateConfiguration = (
    nextConfiguration: AiRoleRoutingConfiguration,
  ) => {
    setConfigurations((current) =>
      current.map((configuration) =>
        configuration.role === nextConfiguration.role
          ? nextConfiguration
          : configuration,
      ),
    );
  };

  const saveConfiguration = async (
    configuration: AiRoleRoutingConfiguration,
  ) => {
    const role = configuration.role;
    const body: UpdateAiRoleRoutingRequest = {
      targetModel: configuration.targetModel,
      inputPrice: configuration.inputPrice,
      outputPrice: configuration.outputPrice,
      mode: configuration.mode,
      fixedProviderId: configuration.fixedProviderId,
    };

    setSavingRole(role);
    try {
      const response = await fetch(`/api/admin/ai-role-routing/${role}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      const payload = (await response.json()) as {
        data?: AiRoleRoutingConfiguration;
        error?: string;
      };
      if (!response.ok || !payload.data) {
        throw new Error(payload.error || 'Routing update failed');
      }
      updateConfiguration(payload.data);
      setCapabilityResults((current) => ({ ...current, [role]: [] }));
      setEditingConfiguration(null);
      toast.success(t('adminModelMappings:messages.updated'));
    } catch (error) {
      console.error('Failed to save AI role routing:', error);
      toast.error(
        error instanceof Error
          ? error.message
          : t('adminModelMappings:errors.save'),
      );
    } finally {
      setSavingRole(null);
    }
  };

  const checkCapabilities = async (role: AiRoutingRole) => {
    const configuration = configurations.find((item) => item.role === role);
    if (!configuration) return;

    const body: CheckModelCapabilitiesRequest = {
      targetModel: configuration.targetModel,
    };
    setCheckingRole(role);
    try {
      const response = await fetch(
        `/api/admin/ai-role-routing/${role}/check-capabilities`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(body),
        },
      );
      const payload = (await response.json()) as {
        data?: ProviderModelCapabilityResult[];
        error?: string;
      };
      if (!response.ok || !payload.data) {
        throw new Error(payload.error || 'Capability check failed');
      }
      setCapabilityResults((current) => ({
        ...current,
        [role]: payload.data,
      }));
    } catch (error) {
      console.error('Failed to check model capabilities:', error);
      toast.error(
        error instanceof Error
          ? error.message
          : t('adminModelMappings:errors.capabilities'),
      );
    } finally {
      setCheckingRole(null);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader
          title={t('adminModelMappings:title')}
          description={t('adminModelMappings:description')}
        />
        <Skeleton className="h-36 rounded-lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('adminModelMappings:title')}
        description={t('adminModelMappings:description')}
      />

      <ul className="divide-y overflow-hidden rounded-lg border bg-background">
        {orderedConfigurations.map((configuration) => (
          <RoleRoutingRow
            key={configuration.role}
            configuration={configuration}
            providers={providers}
            checking={checkingRole === configuration.role}
            capabilityResults={capabilityResults[configuration.role] || []}
            onCheckCapabilities={() =>
              void checkCapabilities(configuration.role)
            }
            onEdit={() => setEditingConfiguration({ ...configuration })}
          />
        ))}
      </ul>

      <EditRoutingDialog
        configuration={editingConfiguration}
        providers={providers}
        saving={savingRole === editingConfiguration?.role}
        onChange={setEditingConfiguration}
        onClose={() => setEditingConfiguration(null)}
        onSave={() => {
          if (editingConfiguration) {
            void saveConfiguration(editingConfiguration);
          }
        }}
      />
    </div>
  );
}
